<template>
  <div ref="chartContainer" style="width: 100%; height: 1000px;"></div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'BarStackChart',
  props: {
    chartData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      chartInstance: null
    };
  },
  watch: {
    chartData: {
      handler() {
        this.$nextTick(this.initChart);
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    initChart() {
      if (!this.chartData || !this.chartData.series || !this.chartData.xAxisData) return;

      const container = this.$refs.chartContainer;
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(container);
        window.addEventListener('resize', this.chartInstance.resize);
      }

      const {series, xAxisData} = this.chartData;

      // 时间戳格式化为年月
      const formattedX = xAxisData.map(ts => {
        const d = new Date(ts * 1000);
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      });

      // 拆分出总金额、折线图、分类柱图
      const totalSeries = series.find(s => s.name === '总金额');
      const lineSeries = series.filter(s => s.type === 'line');
      const categorySeries = series.filter(s => !['总金额', '月累加比例', '单月比例'].includes(s.name));
      // 自定义颜色数组（50个高区分度颜色）
      const categoryColors = [
        '#5470c6', '#91cc75', '#fac858', '#ee6666',
        '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
        '#ea7ccc', '#1e90ff', '#ff6b6b', '#c5f0a4',
        '#ffa502', '#70a1ff', '#7bed9f', '#5352ed',
        '#ff4757', '#2ed573', '#ff6b81', '#ffc0cb',
        '#ffd700', '#ff69b4', '#ff1493', '#ff8c00',
        '#ff00ff', '#800080', '#4b0082', '#00ffff',
        '#008080', '#008000', '#006400', '#2f4f4f',
        '#00008b', '#4169e1', '#0000cd', '#00aecd',
        '#cd0000', '#0033ff', '#19ff00', '#e5ff00',
        '#cd006d', '#8a2be2', '#a52a2a', '#dc143c',
        '#00ff7f', '#ff6347', '#40e0d0', '#9370db',
        '#ff00ff', '#1e90ff', '#b22222', '#228b22',
        '#daa520', '#808000', '#7b68ee'
      ];

      const option = {
        // 关键修正：将自定义颜色数组放在顶层配置中
        color: categoryColors,  // <-- 这里添加自定义颜色数组
        tooltip: {
          trigger: 'axis',
          axisPointer: {type: 'shadow'},
          formatter: function (params) {
            let result = `${params[0].name}<br>`;
            let total = 0;
            let hasValidData = false;

            params.forEach(param => {
              // 过滤掉值为0或空的数据
              if (param.value != null && param.value !== 0) {
                // 折线图不带单位
                const unit = param.seriesType === 'line' ? '' : '元';
                result += `${param.marker} ${param.seriesName}: ${param.value.toFixed(2)}${unit}<br>`;

                // 仅统计柱图数据（排除总金额和折线图）
                if (param.seriesType === 'bar' && param.seriesName !== '总金额') {
                  total += param.value;
                  hasValidData = true;
                }
              }
            });

            // 仅当有有效数据时才显示统计信息
            if (hasValidData) {
              const totalBar = params.find(p => p.seriesName === '总金额');
              result += `<hr style="margin: 5px 0; border-top: 1px dashed #ccc;">`;
              result += `<strong>分类总计: ${total.toFixed(2)}元</strong><br>`;

              if (totalBar && totalBar.value != null) {
                result += `<strong>总金额: ${totalBar.value.toFixed(2)}元</strong>`;
              }
            }

            return result;
          }
        },
        legend: {
          type: 'scroll',
          bottom: 10
        },
        grid: {
          left: '3%',
          right: '6%',
          bottom: '15%',
          top: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: formattedX,
          axisLabel: {
            interval: 0,
            rotate: 30
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '金额（元）'
          },
          {
            type: 'value',
            name: '比例',
            min: 0,
            max: 1,
            axisLabel: {
              formatter: '{value}'
            }
          }
        ],
        series: [
          // 总金额 - 背景柱子
          {
            name: '总金额',
            type: 'bar',
            barGap: '-100%',
            itemStyle: {
              color: 'transparent'
            },
            emphasis: {
              itemStyle: {
                color: 'rgba(0,0,0,0.1)'
              }
            },
            data: totalSeries?.data || []
          },
          // 分类堆叠柱图
          ...categorySeries
              .filter(s => s.data.some(v => v !== 0 && v !== null))
              .map(s => ({
                name: s.name,
                type: 'bar',
                stack: '分类',
                emphasis: {focus: 'series'},
                data: s.data
              })),
          // 折线图部分 - 绑定 yAxisIndex: 1
          ...lineSeries.map(s => ({
            name: s.name,
            type: 'line',
            yAxisIndex: 1,
            data: s.data,
            smooth: true
          }))
        ]
      };

      this.chartInstance.setOption(option);
    }
  },
  beforeDestroy() {
    if (this.chartInstance) {
      window.removeEventListener('resize', this.chartInstance.resize);
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  }
};
</script>